import { Button, Spinner } from 'components/common';
import { AddToCart } from 'lib/cart';
import { Product, ProductCoverImage, Variant } from 'lib/product';
import * as utils from 'lib/product/utils';
import { NormalProductNutrition } from '.';
import ProductLink from '../ProductLink';
import CustomizableProductNutrition from './CustomizableProductNutrition';
import cn from 'classnames';
import { useMemo } from 'react';

interface NewProductCardProps {
  product: Product;
  onShowQuickView?: (
    initialAddonOptions?: Record<number, number>,
  ) => void;
  className?: string;
  initialAddonOptions?: {
    id: string;
    value: string;
    price: number;
  }[];
  onFirstItemAdded?: () => void;
}

const NewProductCard = ({
  product,
  onShowQuickView,
  className,
  initialAddonOptions,
  onFirstItemAdded,
}: NewProductCardProps) => {
  const productVariant = utils.getDisplayingProductVariant(product);
  const isCustomizableProduct = utils.isCustomizableMeal(product);
  const isMerchProduct = utils.isMerch(product);

  const showNutritionValue = useMemo(() => {
    if (isCustomizableProduct) return true;

    const isEmptyNutrition = utils.isEmptyNutritionalValue(
      productVariant?.nutrition ?? null,
    );

    return !isMerchProduct && !isEmptyNutrition;
  }, [isCustomizableProduct, isMerchProduct, productVariant]);

  const { stock, price } = productVariant;
  const productPrice =
    price +
    (initialAddonOptions?.reduce((total, addon) => {
      return total + addon.price;
    }, 0) ?? 0);

  const isOutOfStock = stock === 0;

  return (
    <div
      data-product-id={product.id}
      className={cn(
        'tw-w-full tw-overflow-hidden tw-bg-white tw-transition-shadow tw-shadow-sm tw-flex tw-flex-col hover:tw-shadow',
        className,
      )}
    >
      <figure className="tw-block tw-relative tw-bg-transparent tw-mb-0 tw-overflow-hidden before:tw-block before:tw-content-[''] before:tw-w-full before:tw-bg-cover before:tw-bg-center before:tw-bg-[#f4f4f4] before:tw-pt-[100%] before:tw-bg-gradient-to-r before:tw-from-[#f4f4f4] before:tw-to-[#eee]">
        {isOutOfStock && (
          <span className="tw-absolute tw-top-8 tw-left-8 tw-font-normal tw-text-[1.3rem] tw-leading-snug tw-text-center tw-py-2 tw-px-4 tw-z-[3] tw-text-white tw-bg-primary">
            Oh no! We sold out!
          </span>
        )}
        {product.assets?.length > 0 && (
          <div className="tw-absolute tw-inset-0">
            {/* @ts-ignore */}
            <ProductCoverImage
              alt={product.name}
              productAssets={product.assets}
              layout="responsive"
              size={375}
              onClick={() => onShowQuickView?.()}
              className="tw-cursor-pointer"
            />
            {/* <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4">
              <div className="tw-flex tw-justify-end">
                <ProductLink
                  product={product} // @ts-ignore
                  initialAddonOptions={initialAddonOptions}
                >
                  <div className="tw-text-white tw-font-semibold tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-full tw-bg-white/30 tw-backdrop-blur-sm tw-transition-all">
                    <i className="la la-arrows-v tw-rotate-45" />
                  </div>
                </ProductLink>
              </div>
            </div> */}
          </div>
        )}
      </figure>
      <div className="tw-relative tw-transition-all tw-bg-white tw-space-y-2 tw-flex-1 tw-flex tw-flex-col">
        <div className="tw-flex tw-flex-col tw-flex-1 tw-p-4">
          <h3 className="product-title tw-leading-none">
            <ProductLink
              product={product}
              // @ts-ignore
              initialAddonOptions={initialAddonOptions}
              onClick={(e) => {
                e.preventDefault();
                if (isCustomizableProduct) {
                  onShowQuickView?.(
                    initialAddonOptions?.length
                      ? initialAddonOptions?.reduce((acc, addon) => {
                          acc[Number(addon.id)] = Number(addon.value);
                          return acc;
                        }, {} as Record<number, number>)
                      : undefined,
                  );
                } else {
                  onShowQuickView?.();
                }
              }}
            >
              {product.name}
            </ProductLink>
          </h3>
          {isOutOfStock ? (
            <div
              className={cn(
                'product-price',
                isMerchProduct ? 'tw-mb-0 md:tw-mb-[1.3rem]' : '',
              )}
            >
              <span className="out-price">
                $
                {productPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          ) : (
            <div
              className={cn(
                'product-price tw-font-extrabold tw-text-[#333]',
                isMerchProduct ? 'tw-mb-0 md:tw-mb-[1.3rem]' : '',
              )}
            >
              $
              {productPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          )}

          {product.excerpt && (
            <p
              style={{
                wordWrap: 'break-word',
                WebkitHyphens: 'auto',
                msHyphens: 'auto',
                MozHyphens: 'auto',
                hyphens: 'auto',
              }}
              className="tw-leading-tight tw-text-[12px] tw-break-words tw-hidden md:tw-block"
            >
              {product.excerpt}
            </p>
          )}
          {isCustomizableProduct ? (
            <CustomizableProductNutrition
              productVariant={productVariant}
              initialSelectedAddons={
                initialAddonOptions?.length
                  ? initialAddonOptions?.reduce((acc, addon) => {
                      acc[Number(addon.id)] = Number(addon.value);
                      return acc;
                    }, {} as Record<number, number>)
                  : undefined
              }
            />
          ) : (
            showNutritionValue && (
              <NormalProductNutrition
                productVariant={productVariant}
              />
            )
          )}
        </div>
        <div className="tw-items-end tw-justify-end tw-flex">
          <div className="tw-grid tw-grid-cols-2 tw-items-center tw-w-full">
            {isCustomizableProduct ? (
              <Button
                className="tw-min-w-0 tw-w-full tw-py-3.5 tw-whitespace-nowrap tw-uppercase tw-text-base md:tw-text-lg lg:tw-text-xl lg:tw-py-4 tw-col-span-2 hover:tw-bg-[#1d1d1d] hover:tw-border-[#1d1d1d] focus:tw-bg-[#1d1d1d] focus:tw-border-[#1d1d1d] active:tw-bg-[#1d1d1d] active:tw-border-[#1d1d1d]"
                onClick={() =>
                  onShowQuickView?.(
                    initialAddonOptions?.length
                      ? initialAddonOptions?.reduce((acc, addon) => {
                          acc[Number(addon.id)] = Number(addon.value);
                          return acc;
                        }, {} as Record<number, number>)
                      : undefined,
                  )
                }
              >
                <span className="tw-inline-flex">
                  <i className="bi bi-list-check tw-ml-0 tw-mr-1.5" />
                  Customise Meal
                </span>
              </Button>
            ) : (
              <>
                <Button
                  className="tw-min-w-0 tw-w-full tw-py-3.5 tw-whitespace-nowrap tw-uppercase tw-text-sm sm:tw-text-base md:tw-text-lg lg:tw-text-xl lg:tw-py-4"
                  color="light"
                  onClick={() => onShowQuickView?.()}
                >
                  <span>Quick View</span>
                </Button>
                <AddToCart
                  // @ts-ignore
                  product={product}
                  productVariant={productVariant}
                  quantity={1}
                  onFirstItemAdded={onFirstItemAdded}
                >
                  {({
                    loading: isCartLoading,
                    canAddToCart,
                    addToCart,
                    isAddingToCart,
                  }) => {
                    const disabled =
                      !canAddToCart ||
                      isAddingToCart ||
                      isCartLoading;

                    return (
                      <Button
                        className="tw-min-w-0 tw-py-3.5 tw-w-full tw-uppercase tw-whitespace-nowrap tw-flex tw-items-center tw-text-sm sm:tw-text-base md:tw-text-lg lg:tw-text-xl lg:tw-py-4"
                        disabled={disabled}
                        onClick={() => addToCart()}
                      >
                        <span className="tw-inline-flex tw-items-center">
                          {isAddingToCart ? (
                            <span className="tw-mr-1 tw-inline-flex">
                              <Spinner size="sm" />
                            </span>
                          ) : (
                            <i className="icon-cart-plus tw-ml-0 tw-mr-1.5" />
                          )}
                          {isCartLoading
                            ? 'Loading...'
                            : 'Add to Cart'}
                        </span>
                      </Button>
                    );
                  }}
                </AddToCart>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProductCard;
